import { dataProfile } from "@/data/profiles";
import { useProfile } from "@/hooks/profile";
import { FormData, ProfileProps } from "@/interface/profile";

const Profile: React.FC<ProfileProps> = ({ user }) => {
  const {
    isModalOpen,
    showToast,
    profileData,
    formData,
    closeModal,
    avatarUrl,
    openModal,
    handleChange,
    handleSave,
  } = useProfile({ user });

  return (
    <div className="max-w-5xl mx-auto py-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Profile</h2>

      {/* Kartu Profil */}
      <div className="bg-white border border-gray-200 rounded-[20px] p-6 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6 shadow-sm">
        <div className="flex flex-col md:flex-row items-center gap-6 text-center md:text-left w-full">
          <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-gray-100 flex-shrink-0">
            <img
              src={avatarUrl(profileData.name)}
              alt="Profile Avatar"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex flex-col gap-1">
            <h3 className="text-2xl font-bold text-gray-900">
              {profileData.name}
            </h3>
            <p className="text-gray-500 text-sm font-medium">
              {profileData.email}
            </p>
          </div>
        </div>
        <div className="w-full md:w-auto flex-shrink-0">
          <button
            onClick={openModal}
            className="w-full md:w-auto bg-[#FF0000] hover:bg-red-700 text-white font-bold py-3 px-8 rounded-lg transition-colors text-sm shadow-sm"
          >
            Edit Profile
          </button>
        </div>
      </div>

      {/* Modal Edit Profile */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4"
          onClick={(e) => e.target === e.currentTarget && closeModal()}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 relative animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Edit Profile</h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-700 transition-colors rounded-full hover:bg-gray-100 p-1.5"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Avatar Preview */}
            <div className="flex flex-col items-center mb-6">
              <div className="relative group">
                <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-gray-200">
                  <img
                    src={avatarUrl(formData.name)}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-5 h-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-2">Foto profil</p>
            </div>

            {/* Form Fields */}
            <div className="space-y-4">
              {dataProfile.map((field) => (
                <div key={field.name}>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    {field.label}
                  </label>
                  <input
                    type={field.type}
                    name={field.name}
                    value={formData[field.name as keyof FormData]}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition placeholder-gray-400"
                  />
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-7">
              <button
                onClick={closeModal}
                className="flex-1 border border-gray-200 text-gray-700 font-semibold py-2.5 rounded-lg text-sm hover:bg-gray-50 transition-colors"
              >
                Batal
              </button>
              <button
                onClick={handleSave}
                className="flex-1 bg-[#FF0000] hover:bg-red-700 text-white font-semibold py-2.5 rounded-lg text-sm transition-colors shadow-sm"
              >
                Simpan Perubahan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      <div
        className={`fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-sm font-medium px-5 py-3 rounded-xl shadow-lg transition-opacity duration-300 pointer-events-none z-[60] ${
          showToast ? "opacity-100" : "opacity-0"
        }`}
      >
        ✅ Profil berhasil diperbarui
      </div>
    </div>
  );
};

export default Profile;
